import { useCallback, useEffect, useRef, useState } from "react";
import Map from "ol/Map";
import View from "ol/View";
import TileLayer from "ol/layer/Tile";
import VectorLayer from "ol/layer/Vector";
import VectorSource from "ol/source/Vector";
import OSM from "ol/source/OSM";
import XYZ from "ol/source/XYZ";
import GeoJSON from "ol/format/GeoJSON";
import Draw, { createBox } from "ol/interaction/Draw";
import Modify from "ol/interaction/Modify";
import Select from "ol/interaction/Select";
import Translate from "ol/interaction/Translate";
import Snap from "ol/interaction/Snap";
import type OLCircle from "ol/geom/Circle";
import OLFeature from "ol/Feature";
import type FeatureLike from "ol/Feature";
import { fromCircle } from "ol/geom/Polygon";
import { getArea, getLength } from "ol/sphere";
import { fromLonLat, toLonLat } from "ol/proj";
import { Circle as CircleStyle, Fill, Stroke, Style, Text as TextStyle } from "ol/style";
import { click } from "ol/events/condition";

import type { RascunhoFeicao, GeoJSONGeometry } from "@/types/editor-territorial";
import type { BaseLayerId, DrawMode, EditorFeatureAttrs, InteractionMode, MeasurementResult } from "./editorTypes";

const SETOR_BELA_VISTA_CENTER: [number, number] = [-49.2648, -16.6799];
const MAP_PROJECTION = "EPSG:3857";
const DATA_PROJECTION = "EPSG:4326";

const geojsonFormat = new GeoJSON({
  featureProjection: MAP_PROJECTION,
  dataProjection: DATA_PROJECTION,
});

const STATUS_COLORS: Record<string, string> = {
  RASCUNHO: "#94a3b8",
  EM_REVISAO: "#3b82f6",
  AJUSTAR: "#f59e0b",
  APROVADO: "#22c55e",
  PUBLICADO: "#16a34a",
  REJEITADO: "#ef4444",
  CANCELADO: "#6b7280",
};

const HIDDEN_STYLE = new Style({});

function buildFeatureStyle(hiddenCamadas: Set<string>): (feature: FeatureLike) => Style {
  return (feature: FeatureLike) => {
    const camadaDestino = (feature.get("camadaDestino") as string) || "";
    if (hiddenCamadas.has(camadaDestino)) return HIDDEN_STYLE;

    const status = (feature.get("status") as string) || "RASCUNHO";
    const color = STATUS_COLORS[status] ?? STATUS_COLORS.RASCUNHO;
    const label = (feature.get("tipoObjeto") as string) || "";

    return new Style({
      fill: new Fill({ color: `${color}33` }),
      stroke: new Stroke({ color, width: 2 }),
      image: new CircleStyle({ radius: 6, fill: new Fill({ color }), stroke: new Stroke({ color: "#ffffff", width: 1.5 }) }),
      text: label
        ? new TextStyle({
            text: label,
            font: "11px sans-serif",
            offsetY: -14,
            fill: new Fill({ color: "#1f2937" }),
            stroke: new Stroke({ color: "#ffffff", width: 3 }),
          })
        : undefined,
    });
  };
}

function selectedStyle(): Style {
  return new Style({
    fill: new Fill({ color: "rgba(59,130,246,0.25)" }),
    stroke: new Stroke({ color: "#2563eb", width: 3 }),
    image: new CircleStyle({ radius: 7, fill: new Fill({ color: "#2563eb" }), stroke: new Stroke({ color: "#ffffff", width: 2 }) }),
  });
}

let tempIdCounter = 0;
function generateTempId(): string {
  tempIdCounter += 1;
  return `novo-${Date.now()}-${tempIdCounter}`;
}

export function useEditorMap() {
  const mapDivRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<Map | null>(null);
  const vectorSourceRef = useRef<VectorSource>(new VectorSource());
  const vectorLayerRef = useRef<VectorLayer<VectorSource> | null>(null);
  const hiddenCamadasRef = useRef<Set<string>>(new Set());
  const lockedCamadasRef = useRef<Set<string>>(new Set());
  const activeFeatureIdRef = useRef<string | null>(null);
  const selectInteractionRef = useRef<Select | null>(null);
  const modifyInteractionRef = useRef<Modify | null>(null);
  const translateInteractionRef = useRef<Translate | null>(null);
  const drawInteractionRef = useRef<Draw | null>(null);
  const measureInteractionRef = useRef<Draw | null>(null);
  const snapInteractionRef = useRef<Snap | null>(null);
  const measureSourceRef = useRef<VectorSource>(new VectorSource());
  const boundarySourceRef = useRef<VectorSource>(new VectorSource());

  const [baseLayer, setBaseLayerState] = useState<BaseLayerId>("google-hybrid");
  const [interactionMode, setInteractionModeState] = useState<InteractionMode>("select");
  const [drawMode, setDrawModeState] = useState<DrawMode>("none");
  const [selectedAttrs, setSelectedAttrs] = useState<EditorFeatureAttrs | null>(null);
  const [measurement, setMeasurement] = useState<MeasurementResult | null>(null);
  const [featureCount, setFeatureCount] = useState(0);
  const [pendingProfile, setPendingProfileState] = useState<{ camadaDestino: string; tipoObjeto: string; tipoGeometria: string } | null>(
    null
  );
  const pendingProfileRef = useRef(pendingProfile);
  const setPendingProfile = useCallback((profile: typeof pendingProfile) => {
    pendingProfileRef.current = profile;
    setPendingProfileState(profile);
  }, []);
  const [hiddenCamadas, setHiddenCamadasState] = useState<Set<string>>(new Set());
  const [lockedCamadas, setLockedCamadasState] = useState<Set<string>>(new Set());
  const [layerOpacity, setLayerOpacityState] = useState(1);
  const [cursorCoordinate, setCursorCoordinate] = useState<[number, number] | null>(null);

  const historyRef = useRef<string[]>([]);
  const historyIndexRef = useRef(-1);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const restoringRef = useRef(false);

  const readAttrsFromFeature = useCallback((feature: FeatureLike | undefined): EditorFeatureAttrs | null => {
    if (!feature) return null;
    return {
      featureId: String(feature.getId()),
      rascunhoId: (feature.get("rascunhoId") as string) ?? null,
      camadaDestino: (feature.get("camadaDestino") as string) ?? "",
      tipoObjeto: (feature.get("tipoObjeto") as string) ?? "",
      tipoGeometria: (feature.get("tipoGeometria") as string) ?? "",
      status: (feature.get("status") as string) ?? "RASCUNHO",
      propriedades: (feature.get("propriedades") as Record<string, unknown>) ?? {},
      isNew: Boolean(feature.get("isNew")),
      isDirty: Boolean(feature.get("isDirty")),
    };
  }, []);

  const pushHistory = useCallback(() => {
    if (restoringRef.current) return;
    const snapshot = JSON.stringify(geojsonFormat.writeFeaturesObject(vectorSourceRef.current.getFeatures()));
    const trimmed = historyRef.current.slice(0, historyIndexRef.current + 1);
    trimmed.push(snapshot);
    historyRef.current = trimmed.slice(-50);
    historyIndexRef.current = historyRef.current.length - 1;
    setCanUndo(historyIndexRef.current > 0);
    setCanRedo(false);
    setFeatureCount(vectorSourceRef.current.getFeatures().length);
  }, []);

  const restoreSnapshot = useCallback((index: number) => {
    const snapshot = historyRef.current[index];
    if (snapshot === undefined) return;

    restoringRef.current = true;
    vectorSourceRef.current.clear();
    const features = geojsonFormat.readFeatures(JSON.parse(snapshot));
    vectorSourceRef.current.addFeatures(features);
    restoringRef.current = false;

    historyIndexRef.current = index;
    setCanUndo(index > 0);
    setCanRedo(index < historyRef.current.length - 1);
    setFeatureCount(vectorSourceRef.current.getFeatures().length);
    setSelectedAttrs(null);
    activeFeatureIdRef.current = null;
    selectInteractionRef.current?.getFeatures().clear();
  }, []);

  const undo = useCallback(() => {
    if (historyIndexRef.current > 0) restoreSnapshot(historyIndexRef.current - 1);
  }, [restoreSnapshot]);

  const redo = useCallback(() => {
    if (historyIndexRef.current < historyRef.current.length - 1) restoreSnapshot(historyIndexRef.current + 1);
  }, [restoreSnapshot]);

  // --- map bootstrap -------------------------------------------------------
  useEffect(() => {
    if (!mapDivRef.current || mapRef.current) return;

    const googleSatellite = new TileLayer({
      source: new XYZ({ url: "https://mt0.google.com/vt/lyrs=s&x={x}&y={y}&z={z}", crossOrigin: "anonymous" }),
      visible: baseLayer === "google-satellite",
      properties: { baseLayerId: "google-satellite" },
    });
    const googleHybrid = new TileLayer({
      source: new XYZ({ url: "https://mt0.google.com/vt/lyrs=y&x={x}&y={y}&z={z}", crossOrigin: "anonymous" }),
      visible: baseLayer === "google-hybrid",
      properties: { baseLayerId: "google-hybrid" },
    });
    const googleStreets = new TileLayer({
      source: new XYZ({ url: "https://mt0.google.com/vt/lyrs=m&x={x}&y={y}&z={z}", crossOrigin: "anonymous" }),
      visible: baseLayer === "google-streets",
      properties: { baseLayerId: "google-streets" },
    });
    const osm = new TileLayer({
      source: new OSM(),
      visible: baseLayer === "osm",
      properties: { baseLayerId: "osm" },
    });

    const boundaryLayer = new VectorLayer({
      source: boundarySourceRef.current,
      style: new Style({
        stroke: new Stroke({ color: "#7c3aed", width: 2, lineDash: [8, 6] }),
        fill: new Fill({ color: "rgba(124,58,237,0.05)" }),
      }),
    });

    const vectorLayer = new VectorLayer({
      source: vectorSourceRef.current,
      style: buildFeatureStyle(hiddenCamadasRef.current),
    });
    vectorLayerRef.current = vectorLayer;

    const map = new Map({
      target: mapDivRef.current,
      layers: [googleSatellite, googleHybrid, googleStreets, osm, boundaryLayer, vectorLayer],
      view: new View({
        center: fromLonLat(SETOR_BELA_VISTA_CENTER),
        zoom: 17,
        maxZoom: 21,
      }),
    });

    const select = new Select({
      condition: click,
      style: selectedStyle,
      layers: [vectorLayer],
      filter: (feature) => !lockedCamadasRef.current.has((feature.get("camadaDestino") as string) || ""),
    });
    select.on("select", (event) => {
      const feature = event.selected[0];
      activeFeatureIdRef.current = feature ? String(feature.getId()) : null;
      setSelectedAttrs(readAttrsFromFeature(feature));
    });
    map.addInteraction(select);
    selectInteractionRef.current = select;

    const modify = new Modify({ features: select.getFeatures() });
    modify.setActive(false);
    modify.on("modifyend", () => pushHistory());
    map.addInteraction(modify);
    modifyInteractionRef.current = modify;

    const translate = new Translate({ features: select.getFeatures() });
    translate.setActive(false);
    translate.on("translateend", () => pushHistory());
    map.addInteraction(translate);
    translateInteractionRef.current = translate;

    const snap = new Snap({ source: vectorSourceRef.current });
    map.addInteraction(snap);
    snapInteractionRef.current = snap;

    map.on("pointermove", (event) => {
      const [lon, lat] = toLonLat(event.coordinate);
      setCursorCoordinate([lon, lat]);
    });

    mapRef.current = map;
    pushHistory();

    // O OpenLayers cacheia o tamanho do canvas no momento da criacao do mapa
    // e nao detecta sozinho mudancas de layout (ex.: recolher/expandir o
    // sidebar, que anima a largura via CSS sem disparar "resize" da window).
    // Sem isso, o mapa fica com area em branco/desalinhada apos o toggle.
    // updateSize() imediato cobre o caso do container ainda nao ter o layout
    // final no instante em que o mapa e criado (o ResizeObserver cobre as
    // mudancas de tamanho seguintes, ex.: recolher/expandir o sidebar).
    map.updateSize();
    const resizeObserver = new ResizeObserver(() => map.updateSize());
    resizeObserver.observe(mapDivRef.current);

    return () => {
      resizeObserver.disconnect();
      map.setTarget(undefined);
      mapRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- base layer switching ------------------------------------------------
  const setBaseLayer = useCallback((id: BaseLayerId) => {
    setBaseLayerState(id);
    mapRef.current?.getLayers().forEach((layer) => {
      if (layer instanceof TileLayer) {
        layer.setVisible(layer.get("baseLayerId") === id);
      }
    });
  }, []);

  // --- camada visibility / lock / opacity -----------------------------------
  const toggleCamadaVisibility = useCallback((camadaDestino: string) => {
    setHiddenCamadasState((prev) => {
      const next = new Set(prev);
      if (next.has(camadaDestino)) next.delete(camadaDestino);
      else next.add(camadaDestino);
      hiddenCamadasRef.current = next;
      vectorLayerRef.current?.setStyle(buildFeatureStyle(next));
      return next;
    });
  }, []);

  const toggleCamadaLock = useCallback((camadaDestino: string) => {
    setLockedCamadasState((prev) => {
      const next = new Set(prev);
      if (next.has(camadaDestino)) next.delete(camadaDestino);
      else next.add(camadaDestino);
      lockedCamadasRef.current = next;
      return next;
    });
  }, []);

  const setLayerOpacity = useCallback((value: number) => {
    setLayerOpacityState(value);
    vectorLayerRef.current?.setOpacity(value);
  }, []);

  // --- interaction mode switching ------------------------------------------
  const clearDrawInteraction = useCallback(() => {
    if (drawInteractionRef.current) {
      mapRef.current?.removeInteraction(drawInteractionRef.current);
      drawInteractionRef.current = null;
    }
  }, []);

  const clearMeasureInteraction = useCallback(() => {
    if (measureInteractionRef.current) {
      mapRef.current?.removeInteraction(measureInteractionRef.current);
      measureInteractionRef.current = null;
    }
  }, []);

  const startDraw = useCallback(
    (mode: DrawMode) => {
      clearDrawInteraction();
      if (mode === "none" || !mapRef.current) return;

      const olType = mode === "Rectangle" || mode === "Circle" ? "Circle" : mode;
      const draw = new Draw({
        source: vectorSourceRef.current,
        type: olType as "Point" | "LineString" | "Polygon" | "Circle",
        geometryFunction: mode === "Rectangle" ? createBox() : undefined,
      });

      draw.on("drawend", (event) => {
        const geometry = event.feature.getGeometry();
        if (mode === "Circle" && geometry && "getRadius" in geometry) {
          const polygon = fromCircle(geometry as OLCircle, 64);
          event.feature.setGeometry(polygon);
        }

        const tempId = generateTempId();
        const profile = pendingProfileRef.current;
        event.feature.setId(tempId);
        event.feature.set("rascunhoId", null);
        event.feature.set("status", "RASCUNHO");
        event.feature.set("isNew", true);
        event.feature.set("isDirty", true);
        event.feature.set("camadaDestino", profile?.camadaDestino ?? "");
        event.feature.set("tipoObjeto", profile?.tipoObjeto ?? "");
        event.feature.set("tipoGeometria", profile?.tipoGeometria ?? mode.toUpperCase());
        event.feature.set("propriedades", {});

        activeFeatureIdRef.current = tempId;
        selectInteractionRef.current?.getFeatures().clear();
        selectInteractionRef.current?.getFeatures().push(event.feature);
        setSelectedAttrs(readAttrsFromFeature(event.feature));
        pushHistory();
      });

      mapRef.current.addInteraction(draw);
      drawInteractionRef.current = draw;
    },
    [clearDrawInteraction, pushHistory, readAttrsFromFeature]
  );

  const startMeasure = useCallback(
    (kind: "measure-distance" | "measure-area") => {
      clearMeasureInteraction();
      if (!mapRef.current) return;

      measureSourceRef.current.clear();
      setMeasurement(null);

      const type = kind === "measure-distance" ? "LineString" : "Polygon";
      const draw = new Draw({ source: measureSourceRef.current, type });

      draw.on("drawend", (event) => {
        const geometry = event.feature.getGeometry();
        if (!geometry) return;

        if (kind === "measure-distance") {
          const length = getLength(geometry);
          setMeasurement({
            type: "distance",
            value: length,
            label: length > 1000 ? `${(length / 1000).toFixed(2)} km` : `${length.toFixed(1)} m`,
          });
        } else {
          const area = getArea(geometry);
          setMeasurement({
            type: "area",
            value: area,
            label: area > 10000 ? `${(area / 10000).toFixed(2)} ha` : `${area.toFixed(1)} m²`,
          });
        }

        window.setTimeout(() => measureSourceRef.current.clear(), 4000);
      });

      mapRef.current.addInteraction(draw);
      measureInteractionRef.current = draw;
    },
    [clearMeasureInteraction]
  );

  const setInteractionMode = useCallback(
    (mode: InteractionMode) => {
      setInteractionModeState(mode);
      clearDrawInteraction();
      clearMeasureInteraction();
      modifyInteractionRef.current?.setActive(mode === "modify");
      translateInteractionRef.current?.setActive(mode === "translate");
      selectInteractionRef.current?.setActive(mode !== "draw");

      if (mode === "measure-distance") startMeasure("measure-distance");
      if (mode === "measure-area") startMeasure("measure-area");
    },
    [clearDrawInteraction, clearMeasureInteraction, startMeasure]
  );

  const setDrawMode = useCallback(
    (mode: DrawMode) => {
      setDrawModeState(mode);
      setInteractionModeState("draw");
      selectInteractionRef.current?.setActive(false);
      modifyInteractionRef.current?.setActive(false);
      translateInteractionRef.current?.setActive(false);
      clearMeasureInteraction();
      startDraw(mode);
    },
    [clearMeasureInteraction, startDraw]
  );

  // --- feature operations ---------------------------------------------------
  const deleteSelectedFeature = useCallback(() => {
    if (!activeFeatureIdRef.current) return;
    const feature = vectorSourceRef.current.getFeatureById(activeFeatureIdRef.current);
    if (!feature) return;
    vectorSourceRef.current.removeFeature(feature);
    selectInteractionRef.current?.getFeatures().clear();
    activeFeatureIdRef.current = null;
    setSelectedAttrs(null);
    pushHistory();
  }, [pushHistory]);

  const updateSelectedFeatureProps = useCallback(
    (propriedades: Record<string, unknown>, status?: string) => {
      if (!activeFeatureIdRef.current) return;
      const feature = vectorSourceRef.current.getFeatureById(activeFeatureIdRef.current);
      if (!feature) return;
      feature.set("propriedades", propriedades);
      feature.set("isDirty", true);
      if (status) feature.set("status", status);
      setSelectedAttrs(readAttrsFromFeature(feature));
    },
    [readAttrsFromFeature]
  );

  const markFeatureSaved = useCallback(
    (featureId: string, rascunho: RascunhoFeicao) => {
      const feature = vectorSourceRef.current.getFeatureById(featureId);
      if (!feature) return;
      feature.setId(rascunho.id);
      feature.set("rascunhoId", rascunho.id);
      feature.set("status", rascunho.status);
      feature.set("isNew", false);
      feature.set("isDirty", false);
      feature.set("propriedades", rascunho.propriedades);
      activeFeatureIdRef.current = rascunho.id;
      setSelectedAttrs(readAttrsFromFeature(feature));
    },
    [readAttrsFromFeature]
  );

  const applyStatusToFeature = useCallback(
    (rascunhoId: string, status: string) => {
      const feature = vectorSourceRef.current.getFeatureById(rascunhoId);
      if (!feature) return;
      feature.set("status", status);
      setSelectedAttrs((prev) => (prev && prev.rascunhoId === rascunhoId ? { ...prev, status } : prev));
    },
    []
  );

  const removeFeatureById = useCallback((featureId: string) => {
    const feature = vectorSourceRef.current.getFeatureById(featureId);
    if (feature) vectorSourceRef.current.removeFeature(feature);
    if (activeFeatureIdRef.current === featureId) activeFeatureIdRef.current = null;
    setSelectedAttrs((prev) => (prev && prev.featureId === featureId ? null : prev));
  }, []);

  const loadFeaturesFromRascunhos = useCallback(
    (rascunhos: RascunhoFeicao[]) => {
      vectorSourceRef.current.clear();
      const features = rascunhos
        .filter((r) => r.geom)
        .map((r) => {
          const feature = geojsonFormat.readFeature({
            type: "Feature",
            geometry: r.geom,
            properties: {},
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
          } as any) as FeatureLike;
          feature.setId(r.id);
          feature.set("rascunhoId", r.id);
          feature.set("camadaDestino", r.camadaDestino);
          feature.set("tipoObjeto", r.tipoObjeto);
          feature.set("tipoGeometria", r.tipoGeometria);
          feature.set("status", r.status);
          feature.set("propriedades", r.propriedades);
          feature.set("isNew", false);
          feature.set("isDirty", false);
          return feature;
        });
      vectorSourceRef.current.addFeatures(features);
      setFeatureCount(features.length);
      historyRef.current = [];
      historyIndexRef.current = -1;
      pushHistory();
    },
    [pushHistory]
  );

  const getFeatureGeometryGeoJSON = useCallback((featureId: string) => {
    const feature = vectorSourceRef.current.getFeatureById(featureId);
    if (!feature) return null;
    const geometry = feature.getGeometry();
    if (!geometry) return null;
    return geojsonFormat.writeGeometryObject(geometry) as unknown as { type: string; coordinates: unknown };
  }, []);

  const exportGeoJSON = useCallback(() => {
    return geojsonFormat.writeFeaturesObject(vectorSourceRef.current.getFeatures());
  }, []);

  const setReferenceBoundary = useCallback((geom: GeoJSONGeometry | null) => {
    boundarySourceRef.current.clear();
    if (!geom) return;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const geometry = geojsonFormat.readGeometry(geom as any);
    boundarySourceRef.current.addFeature(new OLFeature({ geometry }));
  }, []);

  const zoomToGeometry = useCallback((geom: GeoJSONGeometry) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const geometry = geojsonFormat.readGeometry(geom as any);
    const view = mapRef.current?.getView();
    if (!view) return;
    view.fit(geometry.getExtent(), { padding: [60, 60, 60, 60], maxZoom: 19, duration: 500 });
  }, []);

  return {
    mapDivRef,
    baseLayer,
    setBaseLayer,
    interactionMode,
    setInteractionMode,
    drawMode,
    setDrawMode,
    selectedAttrs,
    measurement,
    featureCount,
    canUndo,
    canRedo,
    undo,
    redo,
    pendingProfile,
    setPendingProfile,
    hiddenCamadas,
    toggleCamadaVisibility,
    lockedCamadas,
    toggleCamadaLock,
    layerOpacity,
    setLayerOpacity,
    cursorCoordinate,
    deleteSelectedFeature,
    updateSelectedFeatureProps,
    markFeatureSaved,
    applyStatusToFeature,
    removeFeatureById,
    loadFeaturesFromRascunhos,
    getFeatureGeometryGeoJSON,
    exportGeoJSON,
    setReferenceBoundary,
    zoomToGeometry,
  };
}
